import { useSeenOnce } from '../useSeenOnce';

// Read-state for the workout-level coach-notes "?" panel. The header pulses
// the "?" until the athlete opens the panel once.
//
// ⭐ What is LEFT here after R7 (2026-08-17) is the only thing that was ever
// specific to coach notes: the KEY POLICY. The flag is keyed by a content
// hash, NOT by the program/plan id — if the coach edits the note its hash
// changes and the "?" re-pulses, re-surfacing fresh guidance. A control that
// teaches itself once (the ✕ on the gear row) keys by its own name instead and
// is learned forever. Two policies, one store.
//
// The storage — per-device, AsyncStorage on native and localStorage on web —
// moved to `useSeenOnce`, which is why this file no longer has a `.web.ts`
// twin: the platform split lives one level down, once, instead of once per
// caller.
//
// ⚠️ The stored key is UNCHANGED (`jl.coachNotesRead.<hash>`). It is composed
// here and handed over whole, so every flag already on a device survives the
// move — a renamed prefix would silently re-pulse the "?" for everyone who had
// already read their notes.
const KEY_PREFIX = 'jl.coachNotesRead.';

// djb2 — tiny, stable, collision-rate irrelevant for a per-device read flag.
export function hashNotesContent(parts: ReadonlyArray<string | undefined>): string {
  const joined = parts.map((p) => p ?? '').join(' ');
  let h = 5381;
  for (let i = 0; i < joined.length; i++) {
    h = ((h << 5) + h + joined.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

export function useNotesRead(contentKey: string | null): {
  read: boolean;
  markRead: () => void;
} {
  // Null contentKey (a workout with no coach notes) stays null all the way
  // down, where it answers `seen: true` — so a "?" that does not exist can
  // never pulse.
  const { seen, markSeen } = useSeenOnce(
    contentKey ? KEY_PREFIX + contentKey : null,
  );
  return { read: seen, markRead: markSeen };
}
